import React, { useEffect, useRef, useState } from 'react';
import { IoMdArrowDropright } from 'react-icons/io';
import { MdArrowDropDown, MdArrowDropUp, MdClose, MdMenu } from 'react-icons/md';
import { Link } from 'react-router-dom';
import { Button, Image } from '@epcbuilder/lib/components';
import usePreloadImage from '@epcbuilder/lib/hooks/usePreloadImage';

const dropdownItems = {
  About: [
    { name: 'Who We Are', link: '/who-we-are' },
    { name: 'Quality Assurance', link: '/quality-assurance' },
    { name: 'Contact Us', link: '/contactus' },
  ],
  'Our Services ': [
    { name: 'Home Upgrades We Offer', link: '/install-measures' },
    { name: 'Grants & Finance', link: '/finance-and-funding' },
    { name: 'Energy Assessment - What To Expect', link: '/what-to-expect' },
    { name: 'Your Savings', link: '/your-savings' },
  ],
  'Home Upgrades': [
    { name: 'Loft Insulation', link: '/loft-insulation' },
    { name: 'Cavity Wall Insulation', link: '/cavity-wall-insulation' },
    { name: 'Solar PV System', link: '/solar-pv-system' },
    { name: 'Roof Insulation', link: '/roof-insulation' },
    { name: 'Internal Wall Insulation', link: '/internal-wall-insulation' },
  ],
  'Help & FAQs': [
    { name: 'FAQs', link: '/faq' },
    { name: 'Contact Us', link: '/contactus' },
  ],
};

const TopBar = () => {
  usePreloadImage('/images/logo.png');

  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (open) {
      document.body.style.overflowY = 'hidden';
      document.body.style.paddingRight = '5px';
      return () => {
        document.body.style.overflowY = 'scroll';
        document.body.style.paddingRight = '0';
      };
    }
  }, [open]);

  useEffect(() => {
    if (mounted) {
      const mediaQueryList = window.matchMedia('(max-width: 1023px)');
      const handleMediaChange = (event: MediaQueryListEvent) => setIsMobile(event.matches);
      mediaQueryList.addEventListener('change', handleMediaChange);
      setIsMobile(mediaQueryList.matches);
      return () => mediaQueryList.removeEventListener('change', handleMediaChange);
    }
  }, [mounted]);

  if (!mounted) return null;

  return (
    <header
      className={`h-16 bg-[#DFF2EF] ${isMobile && open ? '' : 'md:h-20'} lg:h-24  ${
        open ? 'fixed h-screen lg:relative' : 'relative'
      } lg:bg-light left-0 top-0 z-50 w-full`}
    >
      <div className="fixed flex h-16 w-full items-center justify-between bg-white px-4 shadow-md md:h-20 lg:h-24">
        <div className={`${open && isMobile && 'hidden'} relative h-20 w-36 md:w-48`}>
          <a href="/">
            <Image src="/images/logo.png" alt="EPC Builder Logo" className="size-full object-contain" />
          </a>
        </div>

        {/* Desktop Nav */}
        <div className="font-header hidden items-center gap-8 text-base lg:flex">
          {Object.entries(dropdownItems).map(([title, items]) => (
            <button
              key={title}
              className="group relative text-left"
              onClick={() => setActiveDropdown(activeDropdown === title ? null : title)}
            >
              <span
                className={`flex cursor-pointer flex-row items-center font-semibold ${activeDropdown === title ? 'text-primary-darker' : ''}`}
              >
                {title}
                {activeDropdown === title ? <MdArrowDropUp /> : <MdArrowDropDown />}
              </span>
              {activeDropdown === title && (
                <div className="absolute left-0 z-20 mt-2 w-64 rounded-2xl border-2 border-[#CAE9E4] bg-white shadow-lg">
                  {items.map(({ name, link }) => (
                    <Link
                      key={name}
                      to={link}
                      className="block px-4 py-2 text-[#516B75] first:rounded-t-xl last:rounded-b-xl hover:bg-[#CAE9E4] hover:text-[#227D72]"
                    >
                      <p className="text-[16px] font-normal">{name}</p>
                    </Link>
                  ))}
                </div>
              )}
            </button>
          ))}

          <div className="flex items-center gap-4">
            <a href={`${import.meta.env.VITE_WEB_URL}/register`}>
              <Button
                className="bg-primary-darker border-primary-darker rounded-[10px] px-6 py-2 text-base font-semibold text-white transition"
                overrideBaseClassnames
              >
                Register
              </Button>
            </a>
            <a href={import.meta.env.VITE_WEB_URL}>
              <Button
                className="border-primary-darker text-primary-darker rounded-[10px] border-2 px-6 py-2 text-base font-semibold transition "
                overrideBaseClassnames
              >
                Log In
              </Button>
            </a>
          </div>
        </div>

        {/* Mobile Nav */}
        <div className="flex items-center lg:hidden">
          <div className="text-primary-darker flex size-8 cursor-pointer items-center justify-center">
            <MdMenu size={32} onClick={() => setOpen(true)} />
          </div>
        </div>
      </div>

      {open && (
        <div className="bg-light absolute left-0 top-0 z-50 flex size-full flex-col items-center gap-16 px-8 pt-20 md:flex lg:hidden">
          <div className="text-primary-darker absolute right-4 top-8 cursor-pointer">
            <MdClose size={32} onClick={() => setOpen(false)} />
          </div>
          <div className="flex w-full flex-col items-center gap-8">
            {Object.entries(dropdownItems).map(([title, items]) => (
              <div key={title} className="w-full">
                <button
                  onClick={() => setActiveDropdown(activeDropdown === title ? null : title)}
                  className={`flex w-full items-center justify-between text-left text-[14px] font-bold ${activeDropdown === title ? 'text-primary-darker' : ''}`}
                >
                  {title}
                  {activeDropdown === title ? <MdArrowDropDown size={24} /> : <IoMdArrowDropright size={20} />}
                </button>
                {activeDropdown === title && (
                  <div className="ml-4 mt-2 space-y-2">
                    {items.map(({ name, link }) => (
                      <Link
                        key={name}
                        to={link}
                        onClick={() => setOpen(false)}
                        className="hover:text-primary block text-[14px] text-[#264653]"
                      >
                        {name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="flex w-full flex-col">
            <p className="font-jost mb-2 text-xl font-semibold text-[#264653]">Ready to get started?</p>
            <p className="text-[#516B75]">
              Why waste time searching for various installers, assessors, and finance solutions? We have it all under
              one roof.
            </p>
            <div className="mt-4 flex w-full flex-row gap-4">
              <a href={`${import.meta.env.VITE_WEB_URL}/register`} className="block w-full">
                <Button
                  overrideBaseClassnames={true}
                  style="primary"
                  className="bg-primary-darker border-primary-darker w-full rounded-[10px] px-6 py-2 font-semibold text-white transition"
                >
                  Register
                </Button>
              </a>
              <a href={import.meta.env.VITE_WEB_URL} className="block w-full">
                <Button
                  overrideBaseClassnames={true}
                  style="secondary"
                  className="text-primary-darker border-primary-darker w-full rounded-[10px] border-2 bg-white px-6 py-2 text-base font-semibold transition"
                >
                  Log In
                </Button>
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default TopBar;
